/** Member page — port of the original views.member: totals, hours by project and the member's work log. */
import { useMemo, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { fmtH } from '@/domain/format';
import { useDataset } from '@/hooks/data';
import { PermissionGate } from '@/auth/ProtectedRoute';
import { Avatar, ErrorState, LoadingState, NoRecords, NotFound, StatCard, Topbar } from '@/ui/kit';
import { useAppActions } from '../actions';
import { WorkLogTable } from '../worklog/WorkLogTable';
import { memberSummary } from './memberStats';

export function MemberDetailPage() {
  const { id = '' } = useParams();
  const ds = useDataset();
  const act = useAppActions();
  const member = ds.data?.members.find((m) => m.id === id);
  const d = useMemo(() => (ds.data ? memberSummary(ds.data.logs, id) : null), [ds.data, id]);
  const header = (title: ReactNode, sub?: string) => (
    <Topbar title={title} sub={sub}>
      <Link className="btn" to="/members">
        ← All members
      </Link>
      {member ? (
        <>
          <PermissionGate perm="members:write">
            <button className="btn" onClick={() => act.member({ id })}>
              Edit
            </button>
          </PermissionGate>
          <PermissionGate perm="worklog:write">
            <button className="btn primary" onClick={() => act.workLog({ memberId: id })}>
              + Employee Log Work
            </button>
          </PermissionGate>
        </>
      ) : null}
    </Topbar>
  );
  if (ds.error) return (<>{header('Member')}<ErrorState error={ds.error} onRetry={ds.refetch} /></>);
  if (!ds.data || !d) return (<>{header('Member')}<LoadingState /></>);
  if (!member) return <NotFound what="Member" />;
  const { projects, members } = ds.data;
  const pname = (pid: string) => (pid ? projects.find((p) => p.id === pid)?.name || '—' : 'General');
  return (
    <>
      {header(
        <>
          <Avatar name={member.name} size={34} />
          {member.name}
        </>,
        member.role || '',
      )}
      <div className="cards">
        <StatCard n={fmtH(d.total)} label="Total hours" />
        <StatCard n={d.days} label="Days worked" />
        <StatCard n={d.days ? fmtH(d.total / d.days) : '—'} label="Average per day" />
        <StatCard n={d.projects.length} label="Projects" />
      </div>
      <div className="panel">
        <div className="panel-h">
          <h2>Hours by project</h2>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Project</th>
                <th style={{ width: '40%' }}>Share</th>
                <th className="num">Hours</th>
              </tr>
            </thead>
            <tbody>
              {d.projects.length ? (
                d.projects.map(([pid, h]) => (
                  <tr key={pid}>
                    <td>
                      {pid ? (
                        <Link to={`/projects/${encodeURIComponent(pid)}`} style={{ textDecoration: 'none', fontWeight: 600 }}>
                          {pname(pid)}
                        </Link>
                      ) : (
                        'General'
                      )}
                    </td>
                    <td>
                      <div className="bar">
                        <i style={{ width: `${(h / d.projectHours) * 100}%` }} />
                      </div>
                    </td>
                    <td className="num">{fmtH(h)}</td>
                  </tr>
                ))
              ) : (
                <NoRecords cols={3} />
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="panel">
        <div className="panel-h">
          <h2>Work log</h2>
          <span className="hint">{d.logs.length} entries</span>
        </div>
        <WorkLogTable logs={d.logs} projects={projects} members={members} hideMember />
      </div>
    </>
  );
}
